<template>
  <div class="term-management-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة الفصول الدراسية</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة فصل دراسي جديد
          </el-button>
        </div>
      </template>

      <el-alert
        title="ما هي هذه الواجهة؟"
        type="info"
        description="هذه الواجهة تسمح لك بتقسيم السنة الدراسية إلى فترات زمنية محددة (مثال: الفصل الأول، الفصل الثاني). تعريف هذه الفصول ضروري لتنظيم التقييمات وإصدار الشهادات لاحقًا."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-table :data="terms" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="اسم الفصل الدراسي" />
        <el-table-column prop="study_year.name" label="السنة الدراسية" />
        <el-table-column prop="start_date" label="تاريخ البداية" />
        <el-table-column prop="end_date" label="تاريخ النهاية" />
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm
              title="هل أنت متأكد من حذف هذا الفصل؟"
              confirm-button-text="نعم"
              cancel-button-text="لا"
              @confirm="handleDelete(row.id)"
            >
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Dialog لإنشاء/تعديل الفصل -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="50%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="اسم الفصل الدراسي" prop="name">
          <el-input v-model="form.name" placeholder="مثال: الفصل الأول" />
        </el-form-item>
        <el-form-item label="السنة الدراسية" prop="study_year_id">
          <el-select v-model="form.study_year_id" placeholder="اختر السنة الدراسية" style="width: 100%;">
            <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
          </el-select>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="تاريخ البداية" prop="start_date">
              <el-date-picker v-model="form.start_date" type="date" placeholder="اختر تاريخ البداية" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="تاريخ النهاية" prop="end_date">
              <el-date-picker v-model="form.end_date" type="date" placeholder="اختر تاريخ النهاية" style="width: 100%;" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">
            {{ form.id ? 'تحديث' : 'إنشاء' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';

export default {
  name: 'TermManagement',
  components: { Plus, ElPopconfirm },
  data() {
    return {
      api: null,
      loading: true,
      terms: [],
      studyYears: [],
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      rules: {
        name: [{ required: true, message: 'اسم الفصل مطلوب', trigger: 'blur' }],
        study_year_id: [{ required: true, message: 'السنة الدراسية مطلوبة', trigger: 'change' }],
        start_date: [{ required: true, message: 'تاريخ البداية مطلوب', trigger: 'change' }],
        end_date: [{ required: true, message: 'تاريخ النهاية مطلوب', trigger: 'change' }],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل فصل دراسي' : 'إضافة فصل دراسي جديد';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchInitialData();
  },
  methods: {
    getInitialForm() {
      return { id: null, name: '', study_year_id: null, start_date: '', end_date: '' };
    },
    formatDateForAPI(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        const [termsRes, yearsRes] = await Promise.all([
          this.api.get('/terms'),
          this.api.get('/study-years'),
        ]);
        this.terms = termsRes.data.data || termsRes.data;
        this.studyYears = yearsRes.data.data || yearsRes.data;
      } catch (error) {
        ElMessage.error('فشل في جلب البيانات.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(term) {
      this.form = { ...term };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        
        const payload = {
          ...this.form,
          start_date: this.formatDateForAPI(this.form.start_date),
          end_date: this.formatDateForAPI(this.form.end_date),
        };

        try {
          if (payload.id) {
            await this.api.put(`/terms/${payload.id}`, payload);
            ElMessage.success('تم تحديث الفصل بنجاح!');
          } else {
            await this.api.post('/terms', payload);
            ElMessage.success('تم إنشاء الفصل بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchInitialData(); // Refresh data
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/terms/${id}`);
        ElMessage.success('تم حذف الفصل بنجاح!');
        this.fetchInitialData(); // Refresh data
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في حذف الفصل.');
      }
    },
  },
};
</script>

<style scoped>
.term-management-container {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
